import { z } from "zod";
import { ModelSnapshotSchema } from "./level-assessment-model";

/**
 * Multi-model level-assessment schemas.
 *
 * Axis identifiers and level ranges are NOT hardcoded — they belong to the
 * evaluation model the run was executed against. The result embeds a
 * `modelSnapshot` so a stored or cached result can be re-rendered even after
 * the source model is edited or deleted. Runtime validators that need to
 * check axis ids or clamp levels read those constraints from the snapshot
 * (or the live model when one is loaded).
 */

/** Loose integer level. The model's `levels.min/max` is the real constraint,
 *  validated at usecase boundaries (orchestrator + calibration save). Kept
 *  open here so a shared schema works across models with different ranges. */
export const LevelNumberSchema = z.number().int().min(0);

export const EvidenceSourceTypeSchema = z.enum([
  "career",
  "description",
  "free_text",
  "education",
  "feature",
]);

export const LevelAssessmentEvidenceSchema = z.object({
  sourceType: EvidenceSourceTypeSchema,
  /** Stable reference to the evidence origin — e.g. `feature:cum_T1_years`,
   *  `career:Google/Staff Eng/2019-2022`, `description:profile-headline`. */
  sourceRef: z.string(),
  /** The raw quote / numeric value as observed. */
  quote: z.string(),
  /** Why this evidence maps to the inferred level on this axis. */
  interpretation: z.string(),
});

/** LLM-side schema — what one axis call emits. `axis` is a string axis id;
 *  validation against the model's axis set happens in the orchestrator. */
export const AxisAssessmentLLMSchema = z.object({
  axis: z.string(),
  /** `null` signals abstention — the LLM did not find enough evidence to
   *  pick a level. Abstention is preferred over guessing low. */
  inferredLevel: LevelNumberSchema.nullable(),
  confidence: z.number().min(0).max(1),
  reasoning: z.string(),
  evidence: z.array(LevelAssessmentEvidenceSchema),
});

/** One signal used to score an axis on the per-axis engine — surfaced in
 *  the expanded axis card so the operator can see exactly which signals fed
 *  the score (value + strength tier for THIS run). */
export const SignalUsageSchema = z.object({
  key: z.string(),
  strength: z.enum(["strong", "moderate", "weak"]),
  /** `null` = the extractor explicitly abstained (the LA suite's "unknown" —
   *  routed but not observed; never evidence of absence). */
  value: z.union([z.number(), z.boolean(), z.string(), z.null()]),
});

/** One claim in the per-axis "why this level / why not ±1" comparative: a
 *  short assertion (`point`) backed by a concrete observation (`evidence`).
 *  Powers the 3-column contestability panel in the axis detail. */
export const WhyPointSchema = z.object({
  point: z.string(),
  evidence: z.string(),
});
export type WhyPoint = z.infer<typeof WhyPointSchema>;

/** Where a decisive signal was read from in the candidate's profile — the
 *  citation surfaced in the inline evidence reveal under a decisive-signal
 *  card. `role` is the position (title — company), `meta` the dates/context,
 *  `quote` a faithful excerpt (may be a short summary for derived/numeric
 *  signals that have no single source line). */
export const CriticalEvidenceSchema = z.object({
  role: z.string().default(""),
  meta: z.string().default(""),
  quote: z.string().default(""),
});

/** One signal the explainability layer flagged as CRITICAL (decisive) to the
 *  inferred level on an axis. Ties a routed signal to the rubric
 *  (`rubricConnection`) and back to the source profile (`evidence`) — the
 *  traceability spine surfaced in the axis detail. Carries humanized display
 *  fields (`title`, `valueLabel`, `kind`) so the card never exposes raw
 *  snake_case keys. Produced post-scoring; absent on older results and on
 *  abstaining axes. */
export const CriticalSignalSchema = z.object({
  /** Matches a `signalsUsed` key on the same axis. */
  key: z.string(),
  /** Raw signal value (source of truth — drives `kind`). `null` = unknown
   *  (routed but not observed). */
  value: z.union([z.number(), z.boolean(), z.string(), z.null()]),
  /** Humanized display value, e.g. "Sim" / "Não" / "9,5 anos" / "400+ pessoas". */
  valueLabel: z.string().default(""),
  /** Humanized signal name shown to the reader (no snake_case key). */
  title: z.string().default(""),
  /** Display kind: `yes`/`no` for booleans, `num` for numerics, `text` else. */
  kind: z.enum(["yes", "no", "num", "text"]).default("text"),
  strength: z.enum(["strong", "moderate", "weak"]),
  /** Why this signal was decisive for the inferred level, in plain language
   *  tied to the level's rubric (the signal ↔ rubric bridge). */
  rubricConnection: z.string(),
  /** Profile citations the signal was read from (the signal ↔ profile bridge).
   *  May be empty — the signal still shows with its `rubricConnection`. */
  evidence: z.array(CriticalEvidenceSchema).default([]),
});
export type CriticalSignal = z.infer<typeof CriticalSignalSchema>;

/** The explainer-authored axis narrative — the single user-facing voice for a
 *  competency. Produced by the per-axis explainability call (the same call
 *  that emits `criticalSignals`), AFTER scoring, with full-profile access. The
 *  scoring call's own `reasoning`/`why*`/`ruleApplied` stay on the axis as the
 *  audit trail (explainer grounding + GEPA diagnostics) and as the render
 *  fallback for rows produced before this field shipped. */
export const AxisExplanationSchema = z.object({
  /** Short axis narrative — replaces the raw scoring `reasoning` paragraph. */
  summary: z.string().default(""),
  /** Why the inferred level holds (the held lane of the 3-lane panel). */
  whyThisLevel: z.array(WhyPointSchema).default([]),
  /** Why the candidate does NOT reach one level higher (ceiling lane). */
  whyNotHigher: z.array(WhyPointSchema).default([]),
  /** Why the candidate clears the level just below (floor lane). */
  whyNotLower: z.array(WhyPointSchema).default([]),
});
export type AxisExplanation = z.infer<typeof AxisExplanationSchema>;

/** Result-side schema — tags each axis with its `scope` (cross | vertical)
 *  so the two-section UI can render it under the right tab, carries
 *  `signalsUsed` for the per-axis engine audit, and the contestability fields
 *  (`ruleApplied` + the three why-columns, or `missingEvidence` on abstention)
 *  the panel surfaces on demand. */
export const AxisAssessmentSchema = AxisAssessmentLLMSchema.extend({
  scope: z.enum(["cross", "vertical"]),
  signalsUsed: z.array(SignalUsageSchema).optional(),
  /** Evidence-state confidence band the scorer picked ("convergent" |
   *  "corroborated" | "conjunction_only" | "baseline"). Since 2026-07 the
   *  numeric `confidence` is derived from this band's midpoint — the LLM never
   *  emits a raw float. Absent on results produced before the contract change
   *  and on rounds where confidence is computed mechanically (fusion). */
  confidenceBand: z.string().optional(),
  /** Set when the boundary-agreement pass sampled extra reads that disagreed
   *  on the level — the axis kept its first read with a downgraded band and is
   *  flagged for human review. Absent otherwise. */
  lowAgreement: z.boolean().optional(),
  /** Concise statement of the rubric rule the axis anchored its level on. */
  ruleApplied: z.string().default(""),
  /** Why the inferred level holds — claims backed by profile evidence. */
  whyThisLevel: z.array(WhyPointSchema).default([]),
  /** Why the candidate does NOT reach one level higher. */
  whyNotHigher: z.array(WhyPointSchema).default([]),
  /** Why the candidate clears the level just below (floor justification). */
  whyNotLower: z.array(WhyPointSchema).default([]),
  /** On abstention (`inferredLevel === null`): what evidence was missing. */
  missingEvidence: z.array(z.string()).default([]),
  /** Explainability layer: the routed signals decisive for the inferred level,
   *  each tied to the rubric and to source-profile quotes. Empty on abstention
   *  and on results produced before the layer shipped. */
  criticalSignals: z.array(CriticalSignalSchema).default([]),
  /** The rubric line for the inferred level on this axis — the "matrix excerpt"
   *  the classification connects to. Absent on abstention / older results. */
  levelRubric: z.string().optional(),
  /** Marks this axis as updated by text evidence in a fusion round (vs simply
   *  carried forward) — per-axis provenance for any UI. Absent on round 1 and
   *  on axes that carried their prior read verbatim. */
  evidenceUpdate: z
    .object({
      roundSequence: z.number().int().positive(),
      verdict: z.string(),
    })
    .optional(),
  /** Out-of-band marker for a VERTICAL (discipline) axis whose inferred level
   *  fell outside the discipline's level sub-band: "below" the floor or "above"
   *  the ceiling. The level itself is bounded to a single rung outside the band
   *  (floor−1 / ceiling+1); this flag lets the UI label the competency as
   *  below/above the function's range rather than show a bare lower number.
   *  Absent for in-band axes, cross axes, disciplines with no sub-band, and
   *  results produced before this field shipped. */
  bandStatus: z.enum(["below", "above"]).optional(),
  /** The explainer-authored narrative (summary + 3-lane why-lists). Absent on
   *  abstention, on explainer failure, and on results produced before the
   *  consolidation — readers fall back to the scoring-call fields. */
  explanation: AxisExplanationSchema.optional(),
});
export type AxisAssessment = z.infer<typeof AxisAssessmentSchema>;

/** One section's aggregated result (Cross or Vertical). Sections are
 *  informational: the same weighted mean+dispersion formulas restricted to
 *  the section's axes — purely descriptive; the overall level no longer
 *  derives from them. The vertical section is empty on a cross-only run. */
export const SectionResultSchema = z.object({
  /** Per-axis scores in this section. May be empty for the vertical section
   *  on a cross-only run (model with no discipline targeted). */
  axes: z.array(AxisAssessmentSchema),
  /** Rounded mean level for the section; null when every axis abstained (or
   *  the section is empty). */
  finalLevel: LevelNumberSchema.nullable(),
  confidence: z.number().min(0).max(1),
  /** Raw (unrounded) mean of the present axis levels — the whisker's center.
   *  Null when every axis abstained (or the section is empty). */
  mean: z.number().nullable().default(null),
  /** Population std-dev of the present axis levels — the whisker's half-width.
   *  Null with fewer than 2 scored axes. */
  stddev: z.number().nullable().default(null),
  /** Ids of axes in this section whose `inferredLevel` was null. */
  abstainedAxes: z.array(z.string()),
  /** Retired prose field — rendered nowhere. Optional-with-default so old
   *  persisted rows (which carry the long PT-BR sentence) still deserialize;
   *  new rows write a short factual string or "". */
  rationale: z.string().optional().default(""),
  globalNotes: z.string().optional(),
});
export type SectionResult = z.infer<typeof SectionResultSchema>;

/** One axis's contribution to the overall estimate — its signed `pull` on the
 *  weighted mean (`w′ᵢ·(xᵢ − μ)`, in level units; pulls sum to 0), plus a
 *  plain-language read of what that contribution means for a candidate
 *  otherwise at the estimate. `pull` replaces the v1 `delta` (level − estimate):
 *  what moves the estimate is weight × deviation from the mean, not the raw
 *  level gap. All numbers come from Member B (deterministic attribution); only
 *  `interpretation` is LLM-authored. */
export const AxisContributionSchema = z.object({
  /** Axis id (matches an axis in `cross`/`vertical`). */
  axis: z.string(),
  /** Humanized axis name (no snake_case). */
  axisLabel: z.string(),
  /** The axis's inferred level. */
  level: LevelNumberSchema,
  /** Raw importance weight of the axis on the model (> 0; 1 = flat). */
  weight: z.number().default(1),
  /** Signed pull on the weighted mean (`w′ᵢ·(xᵢ − μ)`), in level units:
   *  negative for down-pullers, positive for up-pullers. */
  pull: z.number().default(0),
  /** Epistemic quality of this axis's read (from per-axis confidence). A
   *  `fragile` low outlier is an uncertainty to investigate, never a
   *  confirmed weakness. */
  reliability: z.enum(["confident", "fragile"]).default("confident"),
  /** Why this axis at its level matters, framed relative to the candidate's own
   *  estimated level (e.g. "Autonomy 3 levels below the L4 estimate — …"). */
  interpretation: z.string().default(""),
});
export type AxisContribution = z.infer<typeof AxisContributionSchema>;

/** How much of the run's intended weight mass was actually assessed, and which
 *  abstained axes account for the gap. Computed deterministically (Member B). */
export const OverallCoverageSchema = z.object({
  /** Fraction (0–1) of the run's intended weight mass that scored. */
  scoredWeightMass: z.number().min(0).max(1).default(1),
  /** Abstained axes with the weight mass each one cost the run. */
  abstained: z
    .array(
      z.object({
        axis: z.string(),
        axisLabel: z.string().default(""),
        weightMassLost: z.number().default(0),
      }),
    )
    .default([]),
});
export type OverallCoverage = z.infer<typeof OverallCoverageSchema>;

/** The spread of the scored axis levels and what a wide gap risks for a
 *  candidate whose mean lands at the estimate. `min`/`max`/`stddev` are computed
 *  deterministically across all scored axes; `risk` is LLM-authored. */
export const OverallDispersionSchema = z.object({
  /** Lowest scored axis level. */
  min: LevelNumberSchema,
  /** Highest scored axis level. */
  max: LevelNumberSchema,
  /** Population std-dev of the scored axis levels (dispersion). */
  stddev: z.number(),
  /** Coarse spread band derived from `stddev`. */
  spread: z.enum(["tight", "moderate", "wide"]),
  /** Risk read of the spread — what the gap (especially a low outlier) implies
   *  for a candidate whose average lands at the estimate. */
  risk: z.string().default(""),
});
export type OverallDispersion = z.infer<typeof OverallDispersionSchema>;

/** Weighted-mean explainability for the OVERALL level: which axes pull the
 *  weighted mean down/up (by signed `pull`), what the spread risks, and how
 *  much of the intended weight mass was actually assessed — all framed
 *  relative to the estimated level. Numbers are Member B's deterministic
 *  attribution; prose is Member C, composed ONLY from the per-axis (Member A)
 *  explanations — no profile access. Produced post-aggregation by a dedicated
 *  LLM call (`level-assessment-overall-explain-service`); absent on older
 *  results and when fewer than 2 axes scored, in which case the UI falls back
 *  to the count-based synthesis. */
export const OverallLevelExplanationSchema = z.object({
  /** One-line synthesis of how the estimate emerges from the weighted mean. */
  summary: z.string().default(""),
  /** Axes with negative pull — dragging the weighted mean down. */
  pullingDown: z.array(AxisContributionSchema).default([]),
  /** Axes with positive pull — lifting the weighted mean up. */
  pullingUp: z.array(AxisContributionSchema).default([]),
  /** Spread of the scored axes + its risk read. Null with fewer than 2 axes. */
  dispersion: OverallDispersionSchema.nullable().default(null),
  /** Weight mass assessed + the abstained axes that account for the gap. */
  coverage: OverallCoverageSchema.default({ scoredWeightMass: 1, abstained: [] }),
});
export type OverallLevelExplanation = z.infer<typeof OverallLevelExplanationSchema>;

/** One axis's compact movement line for `MovementSummarySchema` — from/to +
 *  verdict, no claims, no ledger. `verdict` is kept a PLAIN STRING here (not
 *  the round-update contract's enum) so a persisted result never fails to
 *  parse if that enum evolves later — this is a display projection, not the
 *  audit trail (the full record lives in the `level_assessment_round_updates`
 *  side table, joined by evaluation id when the UI needs depth).
 *
 *  `rationale` is RETAINED for backward-compat (rows persisted before the
 *  round-story section dropped it still carry it) but is no longer populated
 *  or rendered: it was the P3 audit text (which cites internal claim ids), and
 *  scrubbing those ids for display produced garbled prose. The readable
 *  evidence trail now lives only in each competency card's `reasoning`. */
export const MovementSummaryAxisSchema = z.object({
  scope: z.enum(["cross", "vertical"]),
  axisId: z.string(),
  axisLabel: z.string().default(""),
  fromLevel: LevelNumberSchema.nullable(),
  toLevel: LevelNumberSchema.nullable(),
  verdict: z.string(),
  anomalous: z.boolean().default(false),
  rationale: z.string().default(""),
});
export type MovementSummaryAxis = z.infer<typeof MovementSummaryAxisSchema>;

/** A COMPACT movement record embedded directly in `LevelAssessmentResult` so
 *  `/e/:id` can render "what changed since the prior round" self-contained,
 *  without joining the round-update side table. Produced only for a round
 *  that re-scored (fusion phase, round ≥ 2); absent on round 1 and on every
 *  result persisted before this field shipped. The full claims/ledger for a
 *  round live only in `level_assessment_round_updates` (§8 of
 *  `docs/labs/level-assessment/multi-round-update-model.md`). */
export const MovementSummarySchema = z.object({
  roundId: z.string(),
  roundSequence: z.number().int().positive(),
  baseline: z.enum(["prior", "refreshed_profile"]),
  fromMean: z.number().nullable(),
  toMean: z.number().nullable(),
  fromLevel: LevelNumberSchema.nullable(),
  toLevel: LevelNumberSchema.nullable(),
  anomalous: z.boolean().default(false),
  axes: z.array(MovementSummaryAxisSchema),
  narrative: z.string().default(""),
  /** One-sentence synthesis of the round (S3, the narrative layer's Member
   *  C-fusion upgrade — `docs/labs/level-assessment/multi-round-narrative-layer.md`
   *  §2). Optional: absent on rows persisted before the narrative layer
   *  shipped and on any degraded S3 call whose fallback only fills
   *  `narrative`. */
  headline: z.string().optional(),
  /** Up to 4 one-line highlights — only axes that moved or entered this
   *  round. Optional for the same reason as `headline`. */
  axisHighlights: z.array(z.string()).max(4).optional(),
  /** 1–2 sentences bridging to the open-points ledger (the interview
   *  packet) — what to focus on next. Optional for the same reason as
   *  `headline`. */
  nextFocus: z.string().optional(),
});
export type MovementSummary = z.infer<typeof MovementSummarySchema>;

/** Final shape returned by the usecase and rendered by the UI. Two sections
 *  (cross + vertical, informational) plus a weighted overall level. Embeds the
 *  `modelSnapshot` so consumers don't need a live model load to render. */
export const LevelAssessmentResultSchema = z.object({
  cross: SectionResultSchema,
  /** Empty section (no axes, null level) when the run had no discipline. */
  vertical: SectionResultSchema,
  /** Weighted mean across ALL scored axes (cross + vertical), rounded and
   *  clamped — the candidate's estimated level for this model + discipline.
   *  Null only when every axis abstained. */
  overallLevel: LevelNumberSchema.nullable(),
  overallConfidence: z.number().min(0).max(1),
  /** Raw weighted mean across all scored axes — the overall whisker's center.
   *  Null when every axis abstained. (On rows persisted before weighted
   *  aggregation: the equal-weight mean of the section means.) */
  overallMean: z.number().nullable().default(null),
  /** Weighted population std-dev across all scored axes — the overall whisker's
   *  half-width (μ ± σ of the SAME weighted distribution as `overallMean`).
   *  Null with <2 scored axes. `undefined` on rows persisted before weighted
   *  aggregation — the UI falls back to averaging the section std-devs. */
  overallStddev: z.number().nullable().optional(),
  /** Retired prose field — rendered nowhere. Optional-with-default so old
   *  persisted rows still deserialize; new rows write "" or a short note. */
  overallRationale: z.string().optional().default(""),
  /** Mean/dispersion explainability for the overall level — which axes pull the
   *  average down/up and what the spread risks, framed vs the estimated level.
   *  Optional: absent on older persisted rows and on runs with <2 scored axes
   *  (UI falls back to the count-based synthesis). */
  overallExplanation: OverallLevelExplanationSchema.optional(),
  /** Why `overallExplanation` is ABSENT on this run — the degradation reason
   *  (e.g. "LLM call failed after retries", "<2 scored axes"). Persisted so a
   *  degraded evaluation is self-describing (diagnosable from the UI/shared
   *  view, no server-log access needed). Absent when the explanation succeeded
   *  and on rows persisted before this field shipped. */
  overallExplanationError: z.string().optional(),
  aggregationRule: z.string().default("mean+dispersion / section-mean overall"),
  /** Snapshot of the model the run executed against. Lets the UI render
   *  axis labels + level range without re-loading the live model. */
  modelSnapshot: ModelSnapshotSchema,
  /** Compact "what changed since the prior round" record — present only on a
   *  fusion round (round ≥ 2) that re-scored. Absent on round 1 and on every
   *  result persisted before the multi-round update model shipped. */
  movementSummary: MovementSummarySchema.optional(),
});
export type LevelAssessmentResult = z.infer<typeof LevelAssessmentResultSchema>;
